import { ref, readonly } from 'vue';
import axios from 'axios';

/**
 * Feature Flags Composable
 *
 * Two-phase flag loading:
 * 1. Public flags (app-flags) — loaded on boot, no auth required
 * 2. User flags (user-flags) — loaded after login, evaluated per user
 *
 * Flags are cached in localStorage with a 5-minute TTL.
 * isEnabled() lookups are instant (local Map, zero latency).
 *
 * API endpoints (hogc-one-api):
 *   GET /api/featureflag/app-flags   — public, returns { flagName: boolean }
 *   GET /api/featureflag/user-flags  — authenticated, returns { flagName: boolean }
 *   POST /api/featureflag            — admin (ZM+), create flag
 *   PATCH /api/featureflag/:id       — admin (ZM+), update flag
 */

const API_URL = process.env.VUE_APP_API_URL;
const CACHE_KEY = 'feature_flags';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/** @type {import('vue').Ref<Map<string, boolean>>} */
const flags = ref(new Map());

/** @type {import('vue').Ref<boolean>} */
const isLoaded = ref(false);

/**
 * This member's own values: what user-flags returned, plus any dev seeds. App flags only say
 * whether a row is switched on, not who it targets, so they never override these; otherwise a
 * flag targeted at testers reads as on for everyone after any later app-flags load.
 * @type {Record<string, boolean>}
 */
let memberValues = {};

/** What app-flags returned: the values to fall back to when the member's are forgotten. */
let appValues = {};

/**
 * Whether user-flags has answered for this member. Until it has, a flag targeted at someone else
 * can read as on from the app flags, so anything that must reach only its audience waits on this.
 * @type {import('vue').Ref<boolean>}
 */
const memberLoaded = ref(false);

/**
 * Whose values `memberValues` are: the `sub` of the token they were loaded with. Not every sign-out
 * goes through the Logout mixin (an expired session signs out from the 401 interceptor), so the
 * next member on the phone is told apart by this, not by a sign-out having forgotten the last one.
 * @type {string|null}
 */
let memberOwner = null;

/** The user-flags request in flight, so callers racing at start-up share one. */
let userFlagsRequest = null;

/**
 * The `sub` of an access token, or null when the token is not a JWT this can read.
 * @param {string} token
 * @returns {string|null}
 */
function tokenOwner(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload)).sub || null;
  } catch {
    return null;
  }
}

/**
 * Whether the member values held now may belong to someone other than the holder of `token`.
 * Only a known owner that differs counts: an unreadable token cannot tell members apart.
 * @param {string} [token]
 * @returns {boolean}
 */
function heldForSomeoneElse(token) {
  const owner = token ? tokenOwner(token) : null;
  return Boolean(owner && memberOwner && owner !== memberOwner);
}

/**
 * Load cached flags from localStorage if still valid
 */
function loadFromCache() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return false;

    const { data, app, member, owner, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return false;
    }

    flags.value = new Map(Object.entries(data));
    appValues = app || data;
    memberValues = member || {};
    memberOwner = (member && owner) || null;
    memberLoaded.value = Boolean(member);
    isLoaded.value = true;
    return true;
  } catch {
    localStorage.removeItem(CACHE_KEY);
    return false;
  }
}

/**
 * Save flags to localStorage cache
 * @param {Object} flagData - { flagName: boolean } object
 */
function saveToCache(flagData) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        data: flagData,
        app: appValues,
        member: memberLoaded.value ? memberValues : null,
        owner: memberLoaded.value ? memberOwner : null,
        timestamp: Date.now(),
      }),
    );
  } catch {
    // localStorage full or unavailable — continue without cache
  }
}

/**
 * Fetch public app-level flags (no auth required)
 */
async function loadAppFlags() {
  try {
    const { data } = await axios.get(`${API_URL}/featureflag/app-flags`);
    appValues = { ...appValues, ...data };
    const merged = { ...Object.fromEntries(flags.value), ...data, ...memberValues };
    flags.value = new Map(Object.entries(merged));
    saveToCache(merged);
    isLoaded.value = true;
  } catch (error) {
    console.warn('[FeatureFlags] Failed to load app flags:', error?.message);
  }
}

/**
 * Fetch user-specific flags (requires auth token in store)
 * @param {string} token - Bearer token for Authorization header
 * @returns {Promise<boolean>} whether they loaded, so a caller can ask again later
 */
function loadUserFlags(token) {
  if (!token) return Promise.resolve(false);
  if (userFlagsRequest?.token === token) return userFlagsRequest.promise;
  // Someone else signed in: their values must not show while this member's are on the way.
  if (heldForSomeoneElse(token)) forgetMemberFlags();

  const promise = (async () => {
    try {
      const { data } = await axios.get(`${API_URL}/featureflag/user-flags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Overtaken by a sign-out or a newer request while it was in flight: the answer may be for a
      // member who is no longer here.
      if (userFlagsRequest?.promise !== promise) return false;
      memberValues = { ...data };
      memberOwner = tokenOwner(token);
      memberLoaded.value = true;
      const merged = { ...Object.fromEntries(flags.value), ...data };
      flags.value = new Map(Object.entries(merged));
      saveToCache(merged);
      return true;
    } catch (error) {
      console.warn('[FeatureFlags] Failed to load user flags:', error?.message);
      return false;
    } finally {
      if (userFlagsRequest?.promise === promise) userFlagsRequest = null;
    }
  })();
  userFlagsRequest = { token, promise };
  return promise;
}

/**
 * Check if a feature flag is enabled
 * @param {string} flagName
 * @returns {boolean}
 */
function isEnabled(flagName) {
  return flags.value.get(flagName) === true;
}

/**
 * Whether this member's own value of a flag is known (it came from user-flags). Until it is, a
 * flag targeted at someone else can read as on, from the app flags.
 * @param {string} flagName
 * @param {string} [token] the signed-in member's token: a value held for someone else is not theirs
 * @returns {boolean}
 */
function hasMemberValue(flagName, token) {
  return !heldForSomeoneElse(token) && Object.prototype.hasOwnProperty.call(memberValues, flagName);
}

/**
 * Whether this member's own flag values have loaded (see `memberLoaded`).
 * @returns {boolean}
 */
function hasMemberFlags() {
  return memberLoaded.value;
}

/**
 * Raw, three-state value of a flag: true, false, or undefined when the backend
 * has never sent it.
 *
 * `isEnabled()` collapses "off" and "never heard of it" into the same false,
 * which is right for gating a feature and wrong for a kill switch — an outage
 * of the flags API would read as "everybody off". Callers that need to act only
 * on an explicit false use this instead of reaching into the Map.
 *
 * @param {string} flagName
 * @returns {boolean|undefined}
 */
function getFlag(flagName) {
  return flags.value.get(flagName);
}

/**
 * DEV-ONLY: force flags on in the live in-memory Map (and cache) without a
 * backend round-trip. The localStorage cache alone is not enough once App.vue
 * has loaded flags on boot (`isLoaded` true) — route guards read this Map, and
 * `requireFeatureFlag` only calls the API when `isEnabled` is already false, so
 * a disabled dev flag would overwrite a cache-only seed. Setting the Map to true
 * makes `isEnabled` short-circuit. Used by the Next Steps Mock console.
 * @param {Object} flagData - { flagName: boolean }
 */
export function seedDevFlags(flagData) {
  memberValues = { ...memberValues, ...flagData };
  memberLoaded.value = true;
  const merged = { ...Object.fromEntries(flags.value), ...flagData };
  flags.value = new Map(Object.entries(merged));
  isLoaded.value = true;
  saveToCache(merged);
}

/**
 * Sign-out: back to the app flags alone. On native the document outlives sign-out, so without
 * this the next member on the phone starts with this member's values. App flags stay, since the
 * OTA kill switch and channel reads them.
 */
function forgetMemberFlags() {
  memberValues = {};
  memberOwner = null;
  memberLoaded.value = false;
  userFlagsRequest = null;
  flags.value = new Map(Object.entries(appValues));
  saveToCache(appValues);
}

/**
 * Clear all cached flags
 */
function clearFlags() {
  flags.value = new Map();
  memberValues = {};
  memberOwner = null;
  appValues = {};
  memberLoaded.value = false;
  userFlagsRequest = null;
  isLoaded.value = false;
  localStorage.removeItem(CACHE_KEY);
}

/**
 * Composable entry point
 */
export function useFeatureFlags() {
  // Load from cache on first use
  if (!isLoaded.value) {
    loadFromCache();
  }

  return {
    flags: readonly(flags),
    isLoaded: readonly(isLoaded),
    isEnabled,
    hasMemberValue,
    hasMemberFlags,
    getFlag,
    loadAppFlags,
    loadUserFlags,
    forgetMemberFlags,
    clearFlags,
  };
}
